/**
 * SMS и звонки дилерам о старте торгов (СберАвто). Миграции stryzh
 * 20261103160000 (SMS) и 20261105220000 (звонки).
 *
 * Получатели: public.auction_sms_recipients (дилер, ФИО, телефон цифрами).
 * Выключатель и шаблон: system_config (sms_auction_start_enabled,
 * sms_template_auction_start). Отправку делает триггер БД на переходе лида
 * в «Торги»; при выключенном рубильнике он пишет в sms_outbox строки
 * skipped, и они видны в «Коммуникациях» лида.
 */
import { createClient } from '@/api/supabase'

export interface AuctionSmsRecipient {
  id: string
  dealer: string
  full_name: string
  /** Цифры без знаков: 79261115983. */
  phone: string
  is_active: boolean
  /** Тестовый получатель (наш номер): SMS только по тестовым лидам (миграция 20261104180000). */
  is_test: boolean
  /** sms: только SMS; sms_call: SMS и звонок (миграция 20261105220000). */
  channel: AuctionRecipientChannel
  /** Обращение в звонке ({name}), обычно имя. null: без обращения. */
  call_name: string | null
  created_at: string
  updated_at: string
}

export type AuctionRecipientChannel = 'sms' | 'sms_call'

export const AUCTION_SMS_KEYS = {
  enabled: 'sms_auction_start_enabled',
  template: 'sms_template_auction_start',
} as const

/** Должен совпадать с seed миграции 20261103160000 и дефолтом в триггере. */
export const AUCTION_SMS_TEMPLATE_DEFAULT = 'Старт торгов. {car}. До {end} МСК. {link}'

export const AUCTION_SMS_PLACEHOLDERS = ['{car}', '{end}', '{link}'] as const

/** Подстановка как в триггере: нет {end} в данных, предложение с ним выбрасывается. */
export function renderAuctionSms(template: string, v: { car: string; end: string | null; link: string }): string {
  let text = template.trim() || AUCTION_SMS_TEMPLATE_DEFAULT
  text = v.end == null ? text.replace(/\s*[^.\s][^.]*\{end\}[^.]*\./, '') : text.split('{end}').join(v.end)
  text = text.split('{car}').join(v.car).split('{link}').join(v.link)
  return text.replace(/\s+/g, ' ').trim()
}

/**
 * Звонки о старте торгов (миграция stryzh 20261105220000). Очередь ставит
 * триггер БД в call_outbox получателям с каналом sms_call, звонит робот.
 */
export const AUCTION_CALL_KEYS = {
  enabled: 'call_auction_start_enabled',
  template: 'call_template_auction_start',
  windowFrom: 'call_window_from',
  windowTo: 'call_window_to',
} as const

/** Должен совпадать с seed миграции 20261105220000 и дефолтом в триггере. */
export const AUCTION_CALL_TEMPLATE_DEFAULT =
  '{name} здравствуйте, это Карлинк. Новый лот Сберавто на торгах. {car} {year}. Ссылка - в смс.'

export const AUCTION_CALL_PLACEHOLDERS = ['{name}', '{car}', '{year}'] as const

/** Окно звонков по умолчанию и жёсткие границы (ночью не звоним), как в call_window_next(). */
export const AUCTION_CALL_WINDOW_DEFAULT = { from: '09:00', to: '20:00' } as const
export const AUCTION_CALL_WINDOW_LIMIT = { from: '08:00', to: '21:00' } as const

/** «ЧЧ:ММ» внутри жёстких границ и from < to. Строки одного формата сравниваются как время. */
export function isValidCallWindow(from: string, to: string): boolean {
  const re = /^([01]\d|2[0-3]):[0-5]\d$/
  return (
    re.test(from) &&
    re.test(to) &&
    from < to &&
    from >= AUCTION_CALL_WINDOW_LIMIT.from &&
    to <= AUCTION_CALL_WINDOW_LIMIT.to
  )
}

/** Подстановка как render_auction_call() в БД: пустые плейсхолдеры не оставляют лишних пробелов и знаков. */
export function renderAuctionCall(template: string, v: { name: string; car: string; year: string }): string {
  let s = template.trim() || AUCTION_CALL_TEMPLATE_DEFAULT
  s = s.split('{name}').join(v.name.trim()).split('{car}').join(v.car.trim()).split('{year}').join(v.year.trim())
  s = s
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.!?])/g, '$1')
    .replace(/([.,!?])[.,]+/g, '$1')
    .trim()
    .replace(/^[ ,.]+/, '')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** «Горшков Игорь Михайлович» → «Игорь», как бэкфилл миграции 20261105220000. */
export function guessCallName(fullName: string): string | null {
  const words = fullName.trim().split(/\s+/)
  return words.length >= 2 ? words[1] : null
}

export async function listAuctionSmsRecipients(): Promise<AuctionSmsRecipient[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('auction_sms_recipients')
    .select('*')
    .order('dealer')
    .order('full_name')
  if (error) throw new Error(error.message)
  return (data ?? []) as AuctionSmsRecipient[]
}

export async function upsertAuctionSmsRecipient(
  r: Pick<AuctionSmsRecipient, 'dealer' | 'full_name' | 'phone' | 'is_active'> & {
    id?: string
    is_test?: boolean
    channel?: AuctionRecipientChannel
    call_name?: string | null
  },
): Promise<AuctionSmsRecipient> {
  const supabase = createClient()
  const row = { ...r, updated_at: new Date().toISOString() }
  const { data, error } = await supabase.from('auction_sms_recipients').upsert(row).select('*').single()
  if (error) throw new Error(error.message)
  return data as AuctionSmsRecipient
}

export async function deleteAuctionSmsRecipient(id: string): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase.from('auction_sms_recipients').delete().eq('id', id)
  if (error) throw new Error(error.message)
}
